import React, { useMemo, useState } from 'react';
import { AutoComplete, Button, Checkbox, Dropdown, Flex, Modal, Select, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { ContainerOutlined, DownOutlined, TagsOutlined, TeamOutlined } from '@ant-design/icons';
import { useStore } from '../../../inbox-store';
import { useFeature } from '../../../../common/FeatureGate';

const { Text } = Typography;

type Picker = 'group' | 'tag' | null;

interface BulkActionBarProps {
  /** Currently loaded conversation rows (drives the select-all checkbox). */
  conversations: any[];
  /** Idle content rendered when nothing is selected (the panel title). */
  children?: React.ReactNode;
}

const NEW_PREFIX = 'new:';

/**
 * Header strip for the conversation list: a select-all checkbox, and — once
 * one or more rows are checked — "N selected", an Actions dropdown
 * (Add to group / Add tag) and Clear. Pickers open as small modals.
 *
 * Selection state and the bulk calls live in inbox-store; this component is
 * presentation + picker state only. Rows repaint via socket events.
 */
const BulkActionBar: React.FC<BulkActionBarProps> = ({ conversations, children }) => {
  const canBulk = useFeature('contacts');
  const selectedContacts = useStore((s) => s.selectedContacts);
  const setContactsSelected = useStore((s) => s.setContactsSelected);
  const clearSelection = useStore((s) => s.clearSelection);
  const groups = useStore((s) => s.groups);
  const tags = useStore((s) => s.tags);
  const bulkAddTagToSelected = useStore((s) => s.bulkAddTagToSelected);
  const bulkAddSelectedToGroup = useStore((s) => s.bulkAddSelectedToGroup);
  const bulkCreateGroupWithSelected = useStore((s) => s.bulkCreateGroupWithSelected);
  const bulkArchiveSelected = useStore((s) => s.bulkArchiveSelected);
  // Subscribe to the query (not the derived fn) so the label flips on view change.
  const lastListQuery = useStore((s) => s.lastListQuery);
  const isArchiveView = useMemo(
    () => !!lastListQuery?.filters?.some((f: any) => f && f.type === 'top' && f.id === 'archive'),
    [lastListQuery],
  );

  const [picker, setPicker] = useState<Picker>(null);
  const [archiving, setArchiving] = useState(false);
  const [groupValue, setGroupValue] = useState<string | undefined>(undefined);
  const [groupSearch, setGroupSearch] = useState('');
  const [tagValue, setTagValue] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const selectedCount = selectedContacts.size;
  const loadedRows = useMemo(
    () => (conversations || []).filter((c) => c && c.contact_id),
    [conversations],
  );
  const loadedSelectedCount = loadedRows.filter((c) => selectedContacts.has(c.contact_id)).length;
  const allLoadedSelected = loadedRows.length > 0 && loadedSelectedCount === loadedRows.length;
  const someLoadedSelected = loadedSelectedCount > 0 && !allLoadedSelected;

  const selectedNames = useMemo(() => {
    const names = [...selectedContacts.values()].map((c) => c.name).filter(Boolean);
    const head = names.slice(0, 3).join(', ');
    const rest = names.length - 3;
    return rest > 0 ? `${head} +${rest} more` : head;
  }, [selectedContacts]);

  const groupOptions = useMemo(() => {
    const q = groupSearch.trim().toLowerCase();
    const opts = (groups || [])
      .filter((g: any) => !q || String(g.name || '').toLowerCase().includes(q))
      .map((g: any) => ({ value: g.id, label: g.name }));
    const exact = (groups || []).some((g: any) => String(g.name || '').toLowerCase() === q);
    if (q && !exact) {
      opts.push({ value: `${NEW_PREFIX}${groupSearch.trim()}`, label: `Create "${groupSearch.trim()}"` });
    }
    return opts;
  }, [groups, groupSearch]);

  const tagOptions = useMemo(() => {
    const q = tagValue.trim().toLowerCase();
    const seen = new Set<string>();
    return (tags || [])
      .map((t: any) => t.value)
      .filter((v: string) => {
        if (!v) return false;
        const key = v.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return !q || key.includes(q);
      })
      .map((v: string) => ({ value: v }));
  }, [tags, tagValue]);

  const closePicker = () => {
    setPicker(null);
    setGroupValue(undefined);
    setGroupSearch('');
    setTagValue('');
  };

  const onToggleAll = () => {
    setContactsSelected(
      loadedRows.map((c) => ({ contact_id: c.contact_id, name: c.name })),
      !allLoadedSelected,
    );
  };

  const submitGroup = async () => {
    if (!groupValue) return;
    setSubmitting(true);
    let ok = false;
    if (groupValue.startsWith(NEW_PREFIX)) {
      ok = await bulkCreateGroupWithSelected(groupValue.slice(NEW_PREFIX.length));
    } else {
      const name = (groups || []).find((g: any) => g.id === groupValue)?.name;
      ok = await bulkAddSelectedToGroup(groupValue, name);
    }
    setSubmitting(false);
    if (ok) closePicker();
  };

  const submitTag = async () => {
    const value = tagValue.trim();
    if (!value) return;
    setSubmitting(true);
    const result = await bulkAddTagToSelected(value);
    setSubmitting(false);
    if (result) closePicker();
  };

  const menuItems: MenuProps['items'] = [
    { key: 'group', icon: <TeamOutlined />, label: 'Add to group' },
    { key: 'tag', icon: <TagsOutlined />, label: 'Add tag' },
    { type: 'divider' },
    {
      key: 'archive',
      icon: <ContainerOutlined />,
      label: isArchiveView ? 'Move back to Inbox' : 'Archive',
      className: 'gh-bulk-archive',
    },
  ];

  const onMenuClick = async ({ key }: { key: string }) => {
    if (key === 'archive') {
      // No picker: archive/unarchive is a direct, reversible action.
      setArchiving(true);
      await bulkArchiveSelected(!isArchiveView);
      setArchiving(false);
      return;
    }
    setPicker(key as Picker);
  };

  if (!canBulk) {
    return <>{children}</>;
  }

  return (
    <>
      {/* lineHeight: the inbox header sets lineHeight 3, which inflates the
          "N selected" text box and pushes the row off-centre. */}
      <Flex
        align="center"
        gap={8}
        style={{ minWidth: 0, width: '100%', lineHeight: 1.5 }}
        className={selectedCount > 0 ? 'gh-bulk-bar' : undefined}
      >
        <Checkbox
          className="gh-bulk-select-all"
          checked={allLoadedSelected}
          indeterminate={someLoadedSelected}
          disabled={loadedRows.length === 0}
          onChange={onToggleAll}
          aria-label="Select all loaded conversations"
        />
        {selectedCount === 0 ? (
          <div style={{ minWidth: 0, flex: 1 }}>{children}</div>
        ) : (
          <>
            <Text strong style={{ whiteSpace: 'nowrap' }} className="gh-bulk-count">
              {selectedCount} selected
            </Text>
            <Dropdown
              menu={{ items: menuItems, onClick: onMenuClick }}
              trigger={['click']}
            >
              <Button size="small" type="primary" className="gh-bulk-actions" loading={archiving}>
                Actions <DownOutlined />
              </Button>
            </Dropdown>
            <Button size="small" type="text" onClick={clearSelection} className="gh-bulk-clear">
              Clear
            </Button>
          </>
        )}
      </Flex>

      <Modal
        title="Add to group"
        open={picker === 'group'}
        onCancel={closePicker}
        onOk={submitGroup}
        okText={groupValue?.startsWith(NEW_PREFIX) ? 'Create & add' : 'Add'}
        okButtonProps={{ disabled: !groupValue, loading: submitting }}
        destroyOnClose
        width={420}
        className="gh-bulk-group-modal"
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Adding {selectedCount} contact{selectedCount === 1 ? '' : 's'}: {selectedNames}
        </Text>
        <Select
          autoFocus
          showSearch
          allowClear
          style={{ width: '100%' }}
          placeholder="Search or create a group"
          value={groupValue}
          options={groupOptions}
          filterOption={false}
          onSearch={setGroupSearch}
          onChange={(v) => setGroupValue(v)}
          notFoundContent={groupSearch ? null : 'No groups yet — type a name to create one'}
        />
      </Modal>

      <Modal
        title="Add tag"
        open={picker === 'tag'}
        onCancel={closePicker}
        onOk={submitTag}
        okText="Add"
        okButtonProps={{ disabled: !tagValue.trim(), loading: submitting }}
        destroyOnClose
        width={420}
        className="gh-bulk-tag-modal"
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Tagging {selectedCount} contact{selectedCount === 1 ? '' : 's'}: {selectedNames}
        </Text>
        <AutoComplete
          autoFocus
          style={{ width: '100%' }}
          placeholder="Pick an existing tag or type a new one"
          value={tagValue}
          options={tagOptions}
          onChange={(v) => setTagValue(v || '')}
          onSelect={(v) => setTagValue(String(v))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && tagValue.trim() && !submitting) {
              e.preventDefault();
              submitTag();
            }
          }}
        />
      </Modal>
    </>
  );
};

export default BulkActionBar;
